import { useCallback, useEffect } from "react";
import { create } from "zustand";

export type Theme = "light" | "dark";

const STORAGE_KEY = "demosim.theme";

function readInitialTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  root.style.colorScheme = theme;
}

// Shared store — every useTheme() call subscribes to the same theme
// value, so a toggle in the sidebar re-renders the hero on the home
// page (and every other consumer) in the same tick. A per-instance
// useState would leave siblings stuck on the previous variant.
interface ThemeStore {
  theme: Theme;
  setTheme: (next: Theme) => void;
  toggle: () => void;
}

const useThemeStore = create<ThemeStore>((set) => ({
  theme: readInitialTheme(),
  setTheme: (next) => set({ theme: next }),
  toggle: () => set((state) => ({ theme: state.theme === "dark" ? "light" : "dark" })),
}));

export function useTheme() {
  const theme = useThemeStore((s) => s.theme);
  const storeSetTheme = useThemeStore((s) => s.setTheme);
  const storeToggle = useThemeStore((s) => s.toggle);

  useEffect(() => {
    applyTheme(theme);
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => storeSetTheme(next), [storeSetTheme]);
  const toggle = useCallback(() => storeToggle(), [storeToggle]);

  return { theme, setTheme, toggle };
}
